import React from "react";
import SEOHead from "../components/SEOHead";
import {
  ClipboardList, Search, Target, LineChart, CheckCircle2, Award, Shield,
  Database, Code, BarChart, FileSearch, Sparkles, Users, Workflow,
  PenTool as Tool, ArrowRight, TrendingUp, MessageSquare,
  Clock, Zap, AlertTriangle, Building2
} from "lucide-react";
import AnimatedSection from "../components/AnimatedSection";
import PageTransition from "../components/PageTransition";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

// Variants d'animation
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 }
  })
};

// Icône simple "timer" pour l'Audit Flash
function TimerIcon(props: any) {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" {...props} className={props.className || "w-6 h-6"}>
      <circle cx="12" cy="13" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M9 3h6" stroke="currentColor" strokeWidth="2" />
      <path d="M12 9v4l3 2" stroke="currentColor" strokeWidth="2" fill="none" />
    </svg>
  );
}

// ---------- JSON-LD (Organization + Service) ----------
const jsonLdAudit = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "Neoqualis",
      "url": "https://neoqualis.com",
      "logo": "https://neoqualis.com/logo.png",
      "contactPoint": [{
        "@type": "ContactPoint",
        "contactType": "customer support",
        "telephone": "+33186046182",
        "email": "contact@neoqualis.com",
        "areaServed": "FR"
      }]
    },
    {
      "@type": "Service",
      "serviceType": "Audit QA & Testing",
      "provider": { "@type": "Organization", "name": "Neoqualis" },
      "areaServed": "FR",
      "description": "Diagnostic de maturité, quick wins 10 jours, roadmap 90 jours."
    }
  ]
};

// ---------- Données de contenu ----------
// const quickProofs = [
//   { label: "+70% de croissance", icon: TrendingUp },
//   { label: "31 consultants & partenaires", icon: Users },
//   { label: "Référentiels ISTQB / Agile", icon: Award },
//   { label: "CI/CD : Cypress • Selenium • JMeter", icon: Code }
// ];

// NOTE: on évite les classes Tailwind dynamiques (bg-${color})
const whyAudit = [
  {
    title: "Anomalies récurrentes avant MEP",
    description: "Pics de défauts en fin de sprint, incidents en production, couverture incomplète.",
    icon: AlertTriangle,
    bgClass: "bg-red-500/10",
    textClass: "text-red-500"
  },
  {
    title: "Cycles de recette trop lents",
    description: "Goulots QA, dette d'automatisation, tests instables qui bloquent les pipelines.",
    icon: Clock,
    bgClass: "bg-orange-500/10",
    textClass: "text-orange-500"
  },
  {
    title: "Risques & conformité",
    description: "Parcours critiques (paiement, KYC) peu sécurisés ou non traçables bout-en-bout.",
    icon: Shield,
    bgClass: "bg-yellow-500/10",
    textClass: "text-yellow-500"
  },
  {
    title: "Rôles & gouvernance flous",
    description: "Synchronisation BA/QA/DevOps insuffisante, reporting peu actionnable.",
    icon: Users,
    bgClass: "bg-blue-500/10",
    textClass: "text-blue-500"
  }
];

const auditScope = [
  {
    title: "Processus QA & gouvernance",
    description: "Rôles/rituels, traçabilité exigences → tests, KPI & reporting.",
    icon: Workflow
  },
  {
    title: "Stratégie & pratiques de test",
    description: "Niveaux/types (fonctionnel, API, mobile), jeux de données & environnements, critères de sortie.",
    icon: Target
  },
  {
    title: "Automatisation & CI/CD",
    description: "Architecture (Cypress/Selenium), déflakiness, exécution en pipeline.",
    icon: Code
  },
  {
    title: "Performance & scalabilité",
    description: "Plan de charge (JMeter), goulots & tuning, observabilité/alerting.",
    icon: BarChart
  },
  {
    title: "Business Analysis",
    description: "Clarté des US & AC, règles de gestion, boucle courte PO ↔ QA.",
    icon: FileSearch
  },
  {
    title: "QualisAI (option)",
    description: "Analyse de specs (cohérence/ambiguïtés), génération Gherkin, export Xray/Squash.",
    icon: Sparkles
  }
];

const deliverables = [
  {
    title: "Rapport d'audit",
    description: "Score de maturité, forces/faiblesses, risques priorisés.",
    icon: ClipboardList
  },
  {
    title: "Quick wins – 10 jours",
    description: "Actions à fort impact immédiat, kits & templates, plan anti-flakiness.",
    icon: Zap
  },
  {
    title: "Roadmap – 90 jours",
    description: "Axes structurants (process/outils/org), objectifs & KPI, plan de déploiement.",
    icon: LineChart
  },
  {
    title: "Playbook & standards",
    description: "Stratégie de test type, modèles Xray/Squash, checklists & Definition of Done.",
    icon: Tool
  }
];

const auditProcess = [
  { step: 1, title: "Cadrage (30 min — offert)", description: "Contexte, objectifs, périmètre, irritants.", icon: Search },
  { step: 2, title: "Collecte & revue", description: "US/specs, plans/cas, rapports, pipelines, métriques (accès lecture suffisant).", icon: Database },
  { step: 3, title: "Interviews ciblées", description: "PO/BA, QA, Dev/DevOps, Ops (30–45 min).", icon: Users },
  { step: 4, title: "Diagnostic & scoring", description: "Grille de maturité (process, outils, orga, perf) + analyse de risques.", icon: ClipboardList },
  { step: 5, title: "Quick wins", description: "Plan d'actions 10 jours (frameworks, tests critiques, reporting, priorisation).", icon: Zap },
  { step: 6, title: "Restitution & roadmap", description: "Démo des reco, livrables & plan 90 jours, Q/R équipe élargie.", icon: LineChart }
];

const auditPacks = [
  {
    title: "Starter",
    subtitle: "Audit flash (30 min offert)",
    description: "Découverte & premiers axes",
    icon: TimerIcon,
    badge: undefined,
    badgeBg: "bg-green-500/10",
    badgeText: "text-green-500",
    features: [
      "Cadrage express & conseils",
      "Checklist de maturité (échantillon)",
      "3 quick wins prioritaires"
    ],
    cta: "Réserver l'appel (30 min)",
    ctaLink: "/contact?type=audit-flash"
  },
  {
    title: "Pro",
    subtitle: "Audit QA complet (1–2 semaines)",
    description: "Diagnostic structuré & quick wins",
    icon: Award,
    badge: "Recommandé",
    badgeBg: "bg-primary/10",
    badgeText: "text-primary",
    features: [
      "Collecte + 4–6 interviews",
      "Scoring détaillé par domaine",
      "Plan 10 jours + roadmap 90 jours",
      "Restitution atelier 1 h"
    ],
    cta: "Demander un devis",
    ctaLink: "/contact?type=audit-pro"
  },
  {
    title: "Enterprise",
    subtitle: "Transformation QA (4–8 semaines)",
    description: "Mise en œuvre & coaching",
    icon: Building2,
    badge: undefined,
    badgeBg: "bg-secondary/10",
    badgeText: "text-secondary",
    features: [
      "Déploiement quick wins & standards",
      "Coaching automation & CI/CD",
      "Performance JMeter (option)",
      "Mesure d'impact & passage de relais"
    ],
    cta: "Parler à un expert",
    ctaLink: "/contact?type=audit-enterprise"
  }
];

const impactMetrics = [
  {
    metric: "–30 à –50%",
    description: "d'anomalies bloquantes pré-MEP",
    icon: Shield,
    bgClass: "bg-green-500/10",
    textClass: "text-green-500"
  },
  {
    metric: "–20 à –35%",
    description: "sur le temps de cycle de recette",
    icon: Clock,
    bgClass: "bg-blue-500/10",
    textClass: "text-blue-500"
  },
  {
    metric: "+20 à +40%",
    description: "de taux d'automatisation utile",
    icon: TrendingUp,
    bgClass: "bg-purple-500/10",
    textClass: "text-purple-500"
  }
];

const Audit: React.FC = () => {
  return (
    <PageTransition>
      <SEOHead
        title="Audit QA & Testing — 30 min pour cadrer, 10 jours pour agir | Neoqualis"
        description="Audit QA complet : diagnostic de maturité, quick wins en 10 jours, roadmap 90 jours. Cadrage offert 30 min. Réduisez les anomalies et accélérez vos cycles de recette."
        canonical="https://neoqualis.com/audit"
        jsonLd={jsonLdAudit}
      />

      <div className="pt-16">
        {/* Hero */}
        <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
          {/* Couche image locale */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'url("/hero-bg.webp")',
              backgroundSize: "cover",
              backgroundPosition: "center"
            }}
          />
          {/* Couche gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary opacity-90" />

          <div className="container relative z-10 text-white">
            <AnimatedSection className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">Audit QA & Testing</h1>
              <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90 mb-8">
                30 minutes pour cadrer. 10 jours pour agir. Une roadmap priorisée pour fiabiliser vos mises en production.
              </p>
              <div className="flex flex-col gap-4 justify-center max-w-md mx-auto">
                <Link
                  to="/contact?type=audit"
                  className="bg-white text-primary px-8 py-4 rounded-full font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center justify-center text-lg w-full"
                >
                  Demander un audit QA (30 min)
                  <ArrowRight className="ml-2" />
                </Link>
                <Link
                  to="/expertise"
                  className="px-8 py-4 rounded-full font-semibold border border-white/80 bg-white/10 backdrop-blur hover:bg-white/20 transition-colors inline-flex items-center justify-center text-white text-lg w-full"
                >
                  Voir nos expertises
                  <ArrowRight className="ml-2" />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Preuves rapides */}
        {/* <section className="py-12 bg-gray-50 border-b">
          <div className="container">
            <div className="flex flex-wrap justify-center items-center gap-8">
              {quickProofs.map(({ label, icon: Icon }, i) => (
                <motion.div
                  key={label}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                  className="flex items-center text-gray-700"
                >
                  <Icon className="w-5 h-5 mr-2 text-primary" />
                  <span className="font-semibold">{label}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section> */}

        {/* Pourquoi réaliser un audit QA ? */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Pourquoi réaliser un audit QA ?</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {whyAudit.map(({ title, description, icon: Icon, bgClass, textClass }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full hover:scale-105 transition-transform">
                    <div className="flex items-center mb-4">
                      <div className={`p-3 rounded-full ${bgClass}`}>
                        <Icon className={`w-8 h-8 ${textClass}`} />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Périmètre & livrables */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Périmètre & livrables</h2>
            </AnimatedSection>

            {/* Ce que nous auditons */}
            <div className="mb-16">
              <AnimatedSection className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Ce que nous auditons</h3>
              </AnimatedSection>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {auditScope.map(({ title, description, icon: Icon }, i) => (
                  <motion.div
                    key={title}
                    custom={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={cardVariants}
                  >
                    <div className="card h-full">
                      <div className="flex items-center mb-3">
                        <div className="p-3 rounded-full bg-primary/10">
                          <Icon className="text-primary w-8 h-8" />
                        </div>
                        <h4 className="text-lg font-semibold ml-4 text-gray-900">{title}</h4>
                      </div>
                      <p className="text-gray-700">{description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Ce que vous recevez */}
            <div>
              <AnimatedSection className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Ce que vous recevez</h3>
              </AnimatedSection>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                {deliverables.map(({ title, description, icon: Icon }, i) => (
                  <motion.div
                    key={title}
                    custom={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={cardVariants}
                  >
                    <div className="card h-full">
                      <div className="flex items-center mb-3">
                        <div className="p-3 rounded-full bg-secondary/10">
                          <Icon className="text-secondary w-8 h-8" />
                        </div>
                        <h4 className="text-lg font-semibold ml-4 text-gray-900">{title}</h4>
                      </div>
                      <p className="text-gray-700">{description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Comment se déroule l'audit ? */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Comment se déroule l'audit ?</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {auditProcess.map(({ step, title, description, icon: Icon }, i) => (
                <motion.div
                  key={step}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full text-center">
                    <div className="flex items-center justify-center mb-4">
                      <div className="relative">
                        <div className="p-3 rounded-full bg-primary/10">
                          <Icon className="text-primary w-8 h-8" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {step}
                        </div>
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-900">{title}</h3>
                    <p className="text-gray-700 text-sm">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Packs */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Packs</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {auditPacks.map(({ title, subtitle, description, icon: Icon, badge, badgeBg, badgeText, features, cta, ctaLink }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full hover:scale-105 transition-transform relative">
                    {badge && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-primary text-white text-sm px-4 py-1 rounded-full font-semibold">
                          {badge}
                        </span>
                      </div>
                    )}
                    <div className="text-center mb-6">
                      <div className="flex justify-center mb-4">
                        <div className={`p-3 rounded-full ${badgeBg}`}>
                          <Icon className={`w-8 h-8 ${badgeText}`} />
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
                      <p className="text-lg text-gray-600 mb-2">{subtitle}</p>
                      <p className="text-gray-700">{description}</p>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-gray-700">
                          <CheckCircle2 className="w-5 h-5 text-secondary mr-3" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={ctaLink} className="btn-primary w-full justify-center">
                      {cta}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Impact à 90 jours */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Impact à 90 jours (ordre de grandeur)</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {impactMetrics.map(({ metric, description, icon: Icon, bgClass, textClass }, i) => (
                <motion.div
                  key={metric}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card text-center">
                    <div className="flex justify-center mb-4">
                      <div className={`p-3 rounded-full ${bgClass}`}>
                        <Icon className={`w-8 h-8 ${textClass}`} />
                      </div>
                    </div>
                    <div className={`text-3xl font-bold ${textClass} mb-2`}>{metric}</div>
                    <p className="text-gray-700">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <AnimatedSection>
              <div className="card bg-blue-50 max-w-4xl mx-auto text-center">
                <p className="text-gray-700">
                  <strong>Note :</strong> Les gains varient selon votre contexte (périmètre, outillage, dette technique, staffing).
                </p>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* FAQ */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">FAQ</h2>
            </AnimatedSection>

            <div className="max-w-4xl mx-auto space-y-6">
              {[
                {
                  q: "Qu'inclut l'échange de 30 minutes ?",
                  a: "Un cadrage du contexte, des objectifs et des irritants. Vous repartez avec 2–3 recommandations rapides et, si besoin, une proposition d'approche."
                },
                {
                  q: "Avez-vous besoin d'accès aux environnements ?",
                  a: "Le plus souvent, un accès lecture aux artefacts suffit (US, plans/cas, pipelines, dashboards). Pour la performance : environnement & jeu de test dédiés recommandés."
                },
                {
                  q: "Travaillez-vous avec nos outils existants ?",
                  a: "Oui : Jira/Xray, Squash TM, Cypress/Selenium, GitLab/Jenkins/Azure DevOps, JMeter… On capitalise d'abord sur l'existant."
                },
                {
                  q: "Quels livrables concrets recevrons-nous ?",
                  a: "Rapport d'audit, plan quick wins (10 jours), roadmap 90 jours, playbook & modèles (stratégie, cas, checklists)."
                },
                {
                  q: "Pouvez-vous nous aider à implémenter ?",
                  a: "Oui : le pack Enterprise inclut la mise en œuvre et le coaching (automation, perfo, gouvernance)."
                }
              ].map(({ q, a }, i) => (
                <motion.div
                  key={q}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card">
                    <h3 className="text-lg font-semibold mb-3 text-gray-900">{q}</h3>
                    <p className="text-gray-700">{a}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center">
              <div className="max-w-3xl mx-auto">
                <h2 className="heading mb-6">Un go-live dans 8 semaines ? Parlons couverture et risques.</h2>
                <div className="flex flex-col gap-4 justify-center max-w-md mx-auto">
                  <Link
                    to="/contact"
                    className="bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-opacity-90 transition-colors inline-flex items-center justify-center text-lg w-full"
                  >
                    Parler à un expert QA
                    <MessageSquare className="ml-2" />
                  </Link>
                  <Link
                    to="/contact?type=audit"
                    className="px-8 py-4 rounded-full font-semibold border border-primary text-primary hover:bg-primary hover:text-white transition-colors inline-flex items-center justify-center text-lg w-full"
                  >
                    Demander un audit QA (offert – 30 min)
                    <ArrowRight className="ml-2" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Audit;
